import { useEffect, useRef, useState } from 'react';
import { LOCALES, useTranslation } from '../../i18n';
import { cn } from '../../lib/utils';
import { CheckIcon, GlobeIcon } from '../icons';

/**
 * Language switcher.
 *
 * Options are labelled with each language's own endonym (English, العربية,
 * Français, Deutsch), which is the standard convention and does not need
 * translating. Selecting a language also flips `<html dir>` for Arabic.
 */
export function LanguageSwitcher() {
  const { locale, setLocale, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const current = LOCALES.find((entry) => entry.code === locale) ?? LOCALES[0];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${t('language.label')}: ${current.label}`}
        title={t('language.label')}
        className="inline-flex h-10 items-center gap-2 rounded-lg px-2 text-muted transition-colors hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <GlobeIcon className="h-5 w-5" />
        <span className="hidden text-sm font-medium lg:inline">{current.label}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-lg"
        >
          {LOCALES.map((option) => (
            <button
              key={option.code}
              type="button"
              role="menuitemradio"
              aria-checked={option.code === locale}
              onClick={() => {
                setLocale(option.code);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-start text-sm',
                'hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand',
                option.code === locale ? 'text-fg' : 'text-muted',
              )}
            >
              <span className="flex-1">{option.label}</span>
              {option.code === locale && <CheckIcon className="h-4 w-4 text-brand" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
